// SCR-FND-08 Change your password at first sign-in (frozen P0 mockup v2, GEN-20, ADR-086).
// Shown right after the first sign-in with a temporary password, before any other page; no menu.
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, Notice } from '../../design/kit';
import { changePassword, Refusal, signOut } from '../../data/auth';
import { Name } from '../../i18n/Isolate';
import { messageKey } from '../../lib/errors';

type Errors = { password?: string; confirm?: string };

export function ChangePassword({ name, onChanged }: { name: string; onChanged: () => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [refusal, setRefusal] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next: Errors = {};
    if (password.length < 8) next.password = t('err.weak');
    else if (confirm !== password) next.confirm = t('err.mismatch');
    setErrors(next);
    setRefusal(undefined);
    if (next.password || next.confirm) return;
    setBusy(true);
    try {
      await changePassword(password);
      onChanged();
    } catch (err) {
      setRefusal(err instanceof Refusal ? err.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  };

  // The name sits in its own <bdi> inside the translated sentence (mixed directions, ADR-082).
  const MARK = '\u0001';
  const [before = '', after = ''] = t('pw.lead', { name: MARK }).split(MARK);
  return (
    <main className="yv-auth">
      <form className="yv-auth__panel" onSubmit={submit} noValidate>
        <div className="yv-brand">
          <span className="yv-brand__mark" aria-hidden="true">
            Y
          </span>
          YakVender
        </div>
        <h1 className="yv-auth__title">{t('s8.title')}</h1>
        <p className="yv-auth__lead">
          {before}
          <Name>{name}</Name>
          {after}
        </p>
        {refusal && <Notice tone={refusal === 'OFFLINE' ? 'warning' : 'danger'}>{t(messageKey(refusal))}</Notice>}
        <Field label={t('pw.new')} hint={t('pass.hint')} error={errors.password}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Field label={t('pw.confirm')} error={errors.confirm}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              aria-describedby={describedBy}
              aria-invalid={invalid}
            />
          )}
        </Field>
        <Button type="submit" variant="main" block busy={busy}>
          {t('pw.save')}
        </Button>
        <Button variant="quiet" block onClick={() => void signOut()}>
          {t('signout')}
        </Button>
      </form>
    </main>
  );
}
